import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

interface ActiveTimer {
  bookingId: string;
  bookingTitle: string;
  bookingNumber: string;
  startTime: Date;
  accumulatedSeconds: number;
}

interface SimpleTimerContextType {
  activeTimer: ActiveTimer | null;
  isLoading: boolean;
  startTimer: (bookingId: string, bookingTitle: string, bookingNumber: string, businessId: string, resourceId: string) => Promise<void>;
  stopTimer: () => Promise<void>;
  getElapsedSeconds: () => number;
}

const SimpleTimerContext = createContext<SimpleTimerContextType | undefined>(undefined);

export const useSimpleTimer = () => {
  const context = useContext(SimpleTimerContext);
  if (!context) {
    throw new Error('useSimpleTimer must be used within a SimpleTimerProvider');
  }
  return context;
};

export function SimpleTimerProvider({ children }: { children: React.ReactNode }) {
  const [activeTimer, setActiveTimer] = useState<ActiveTimer | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentResourceId, setCurrentResourceId] = useState<string | null>(null);

  // Load active timer on mount
  useEffect(() => {
    const loadActiveTimer = async () => {
      if (!supabase) return;

      try {
        // Get current user's resource
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setIsLoading(false);
          return;
        }

        const { data: resource } = await supabase
          .from('resources')
          .select('id')
          .eq('auth_user_id', user.id)
          .single();

        if (!resource) {
          setIsLoading(false);
          return;
        }

        setCurrentResourceId(resource.id);

        // Check for active timer using simple query
        const { data: timerLogs, error } = await supabase
          .from('booking_timer_logs')
          .select(`
            booking_id,
            current_start_time,
            total_seconds,
            bookings!inner(title, booking_number)
          `)
          .eq('resource_id', resource.id)
          .eq('is_running', true)
          .limit(1);

        if (error) {
          console.error('Error loading active timer:', error);
        } else if (timerLogs && timerLogs.length > 0) {
          const timer = timerLogs[0];
          console.log('🔥 Loading active timer from database:', timer);
          console.log('🔥 Setting activeTimer state for booking:', timer.booking_id);
          setActiveTimer({
            bookingId: timer.booking_id,
            bookingTitle: timer.bookings?.title || 'Booking',
            bookingNumber: timer.bookings?.booking_number || timer.booking_id,
            startTime: new Date(timer.current_start_time), // Use database start_time
            accumulatedSeconds: timer.total_seconds || 0 // Use database accumulated_seconds
          });
        } else {
          console.log('🔥 No active timer found in database');
        }
      } catch (error) {
        console.error('Error in loadActiveTimer:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadActiveTimer();
  }, []);

  // Periodic sync with database to ensure timer accuracy
  useEffect(() => {
    if (!activeTimer || !currentResourceId) return;

    const syncWithDatabase = async () => {
      try {
        const { data: timerLogs, error } = await supabase
          .from('booking_timer_logs')
          .select('booking_id, current_start_time, total_seconds')
          .eq('resource_id', currentResourceId)
          .eq('is_running', true)
          .limit(1);

        if (error) {
          console.error('Error syncing timer with database:', error);
          return;
        }

        if (timerLogs && timerLogs.length > 0) {
          const timer = timerLogs[0];
          // Only update if the booking ID matches (timer is still active for same booking)
          if (timer.booking_id === activeTimer.bookingId) {
            setActiveTimer(prev => prev ? {
              ...prev,
              startTime: new Date(timer.current_start_time),
              accumulatedSeconds: timer.total_seconds || 0
            } : null);
          }
        } else {
          // Timer was stopped externally, clear local state
          console.log('Timer was stopped externally, clearing local state');
          setActiveTimer(null);
        }
      } catch (error) {
        console.error('Error in timer sync:', error);
      }
    };

    // Sync every 30 seconds to ensure accuracy
    const syncInterval = setInterval(syncWithDatabase, 30000);

    return () => clearInterval(syncInterval);
  }, [activeTimer?.bookingId, currentResourceId]);

  // Sync when app comes back into focus (handles app close/reopen scenarios)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (!document.hidden && activeTimer && currentResourceId) {
        console.log('App came back into focus, syncing timer with database');
        try {
          const { data: timerLogs, error } = await supabase
            .from('booking_timer_logs')
            .select('booking_id, current_start_time, total_seconds')
            .eq('resource_id', currentResourceId)
            .eq('is_running', true)
            .limit(1);

          if (error) {
            console.error('Error syncing timer on focus:', error);
            return;
          }

          if (timerLogs && timerLogs.length > 0) {
            const timer = timerLogs[0];
            if (timer.booking_id === activeTimer.bookingId) {
              console.log('Updating timer from database on focus:', timer);
              setActiveTimer(prev => prev ? {
                ...prev,
                startTime: new Date(timer.current_start_time),
                accumulatedSeconds: timer.total_seconds || 0
              } : null);
            }
          } else {
            console.log('No active timer found in database, clearing local state');
            setActiveTimer(null);
          }
        } catch (error) {
          console.error('Error in focus sync:', error);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, [activeTimer?.bookingId, currentResourceId]);

  const startTimer = useCallback(async (
    bookingId: string, 
    bookingTitle: string, 
    bookingNumber: string,
    businessId: string,
    resourceId: string
  ) => {
    if (!supabase) return;

    try {
      // If there's an active timer for a different booking, stop it first
      if (activeTimer && activeTimer.bookingId !== bookingId && currentResourceId) {
        console.log('Stopping existing timer for booking:', activeTimer.bookingId);
        await supabase
          .rpc('stop_booking_timer', {
            p_booking_id: activeTimer.bookingId,
            p_resource_id: currentResourceId
          });
      }

      const { data, error } = await supabase
        .rpc('start_booking_timer', {
          p_booking_id: bookingId,
          p_resource_id: resourceId,
          p_business_id: businessId
        });

      if (error) throw error;

      console.log('Timer started in database:', data);

      // Set the active timer using database timestamp
      setActiveTimer({
        bookingId,
        bookingTitle,
        bookingNumber,
        startTime: new Date(data.current_start_time), // Use database timestamp
        accumulatedSeconds: data.total_seconds || 0
      });

      setCurrentResourceId(resourceId);
    } catch (error) {
      console.error('Error starting timer:', error);
      throw error;
    }
  }, [activeTimer, currentResourceId]);

  const stopTimer = useCallback(async () => {
    if (!supabase || !activeTimer || !currentResourceId) return;

    try {
      const { error } = await supabase
        .rpc('stop_booking_timer', {
          p_booking_id: activeTimer.bookingId,
          p_resource_id: currentResourceId
        });

      if (error) throw error;

      // Clear the active timer
      setActiveTimer(null);
    } catch (error) {
      console.error('Error stopping timer:', error);
      throw error;
    }
  }, [activeTimer, currentResourceId]);

  const getElapsedSeconds = useCallback(() => {
    if (!activeTimer) return 0;
    
    const now = new Date();
    const sessionSeconds = Math.floor((now.getTime() - activeTimer.startTime.getTime()) / 1000);
    return activeTimer.accumulatedSeconds + sessionSeconds;
  }, [activeTimer]);

  return (
    <SimpleTimerContext.Provider value={{
      activeTimer,
      isLoading,
      startTimer,
      stopTimer,
      getElapsedSeconds
    }}>
      {children}
    </SimpleTimerContext.Provider>
  );
}
